import { useTranslation } from 'react-i18next';
import { FormControlInput } from '../../../../app/components/FormControls';
import { formatMajorAmount } from '../../application/formatLessonMoney';
import { quotePackagePrice } from '../../application/quotePackagePrice';
import type { LessonPackageDraft } from '../../application/types';

const FIELD_WIDTH = '100%';
const FIELD_HEIGHT = 40;

type Props = {
  offeringPrice: number;
  currency: string;
  value: LessonPackageDraft;
  onChange: (next: LessonPackageDraft) => void;
};

function wholeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

function discountPercent(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  if (parsed >= 100) return null;
  return parsed;
}

export function packageDraftQuote(offeringPrice: number, value: LessonPackageDraft) {
  const quantity = wholeNumber(value.quantity);
  const discount = discountPercent(value.discountPercent);
  if (quantity == null || discount == null) return null;
  return quotePackagePrice(offeringPrice, quantity, discount);
}

export function LessonPackageForm({ offeringPrice, currency, value, onChange }: Props) {
  const { t, i18n } = useTranslation();
  const quote = packageDraftQuote(offeringPrice, value);
  const money = (amount: number) =>
    formatMajorAmount(amount, currency, i18n.language, 2);

  return (
    <div className="mentor-lessons__form">
      <FormControlInput
        name="quantity"
        type="number"
        label={t('lesson.number_of_lessons')}
        value={value.quantity}
        min={2}
        max={100}
        width={FIELD_WIDTH}
        height={FIELD_HEIGHT}
        className="form-field"
        onChange={(quantity) => onChange({ ...value, quantity })}
      />
      <FormControlInput
        name="discountPercent"
        type="number"
        label={t('lesson.package_discount')}
        value={value.discountPercent}
        min={0}
        max={99.99}
        width={FIELD_WIDTH}
        height={FIELD_HEIGHT}
        className="form-field"
        onChange={(discountPercent) => onChange({ ...value, discountPercent })}
      />
      {quote ? (
        <dl className="mentor-lessons__quote">
          <div>
            <dt>{t('lesson.regular_price')}</dt>
            <dd>{money(quote.regularPrice)}</dd>
          </div>
          <div>
            <dt>{t('lesson.discount')}</dt>
            <dd>{value.discountPercent}%</dd>
          </div>
          <div>
            <dt>{t('lesson.you_save')}</dt>
            <dd>{money(quote.savings)}</dd>
          </div>
          <div>
            <dt>{t('lesson.package_price')}</dt>
            <dd>{money(quote.price)}</dd>
          </div>
          <div>
            <dt>{t('lesson.price_per_lesson')}</dt>
            <dd>{money(quote.pricePerLesson)}</dd>
          </div>
        </dl>
      ) : null}
    </div>
  );
}
